import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { useWindowStore } from '~/lib/stores/window-store';
import { shouldMinimizeOnEscape, type WindowKind } from '~/lib/stores/window-rules';

/**
 * FRONT-3146: the plumbing that turns the three full-screen overlays (chat
 * window, Build window, Pro Builder) into windows in the shared layer.
 *
 * A surface keeps its own component and lifecycle; `WindowShell` wraps it.
 * Inside the app shell (`WindowSystemContext` is true) the surface registers a
 * window and renders through a portal into that window's slot in the layer
 * (`window-layer.tsx`), reading `useWindowChrome()` for "am I docked" and the
 * minimize / close / Escape behaviour. Outside it — a component test, a
 * standalone mount — nothing changes: the surface renders as the full-screen
 * overlay it always was. That fallback is deliberate, so the pre-existing
 * renderer tests keep proving the real component.
 */
export const WindowSystemContext = createContext(false);

/** The slot elements the layer renders, by window id. */
interface SlotRegistry {
  slots: Record<string, HTMLElement>;
  setSlot: (id: string, el: HTMLElement | null) => void;
}

export const useWindowSlots = create<SlotRegistry>((set) => ({
  slots: {},
  setSlot: (id, el) =>
    set((s) => {
      if (el === null) {
        if (!(id in s.slots)) return s;
        const next = { ...s.slots };
        delete next[id];
        return { slots: next };
      }
      return s.slots[id] === el ? s : { slots: { ...s.slots, [id]: el } };
    }),
}));

export interface WindowChrome {
  /** Rendered inside the window layer (not the standalone full-screen overlay). */
  docked: boolean;
  windowId: string | null;
  minimize: () => void;
  close: () => void;
  /**
   * What an unclaimed Escape does. Docked: minimize, but only the window that
   * has the person's attention. Standalone: the surface's own close.
   */
  escape: (standaloneClose: () => void) => void;
  /** Keeps the bar's label in step with the surface's own title. */
  setTitle: (title: string) => void;
}

const STANDALONE: WindowChrome = {
  docked: false,
  windowId: null,
  minimize: () => {},
  close: () => {},
  escape: (standaloneClose) => standaloneClose(),
  setTitle: () => {},
};

const WindowChromeContext = createContext<WindowChrome>(STANDALONE);

export function useWindowChrome(): WindowChrome {
  return useContext(WindowChromeContext);
}

export function WindowShell({
  id,
  kind,
  refId,
  title,
  hosted,
  keepMounted = false,
  onDismiss,
  onClose,
  children,
}: {
  id: string;
  kind: WindowKind;
  refId: string | null;
  title: string;
  /**
   * True when the window host created the entry from a request; false for a
   * page-local mount, which registers itself and unregisters on unmount.
   */
  hosted: boolean;
  /**
   * Keep rendering into the slot while the window is not in the store (closed).
   * Only Pro Builder: closing hides it, and its terminal is a running Claude Code.
   */
  keepMounted?: boolean;
  /** What a close from outside the surface (the bar's ×, the chooser's Cancel) means to a local owner. */
  onDismiss?: () => void;
  /** The surface's own close, for the Close button. */
  onClose: () => void;
  children: ReactNode;
}) {
  const available = useContext(WindowSystemContext);
  const state = useWindowStore((s) => s.windows.find((w) => w.id === id)?.state);
  const slot = useWindowSlots((s) => s.slots[id]);

  useEffect(() => {
    if (!available || hosted) return;
    useWindowStore.getState().open({ id, kind, ref: refId, title }, onDismiss);
    return () => useWindowStore.getState().unregister(id);
    // Registration is per mount; title changes are pushed by `setTitle` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, hosted, id]);

  const chrome = useMemo<WindowChrome>(
    () => ({
      docked: true,
      windowId: id,
      minimize: () => useWindowStore.getState().minimize(id),
      close: onClose,
      escape: () => {
        // The window under the mouse is the active one (eddy, 2026-10-26: "the
        // one where the mouse is over should be active, so ESC closes it") —
        // the slot sets it on pointer enter, so keyboard focus sitting in the
        // OTHER column no longer decides. Escape still belongs to any layer
        // above the windows: focus in the command palette, Settings or a
        // dialog (anything outside every window slot) is left alone.
        const slots = Object.values(useWindowSlots.getState().slots);
        const active = document.activeElement;
        const idle = !active || active === document.body || active === document.documentElement;
        if (!idle && !slots.some((el) => el?.contains(active))) return;
        const s = useWindowStore.getState();
        if (shouldMinimizeOnEscape(s, id)) onClose();
      },
      setTitle: (t) => useWindowStore.getState().setTitle(id, t),
    }),
    [id, onClose],
  );

  if (!available) return <>{children}</>;
  // Not registered yet, or waiting on the "which window to minimize?" answer:
  // nothing is mounted, so a pending chat never sends its first message early.
  if ((state === undefined && !keepMounted) || state === 'pending' || !slot) return null;
  return createPortal(<WindowChromeContext.Provider value={chrome}>{children}</WindowChromeContext.Provider>, slot);
}
